import { Sentry } from './sentry';

interface ReportErrorOptions {
  source?: string;
  severity?: 'error' | 'warning' | 'info';
  context?: Record<string, unknown>;
}

/**
 * Unified error reporting utility.
 * Dev: logs to console with [source] prefix.
 * Prod: sends to Sentry with source tag and context.
 * Filters out auth errors to avoid noise.
 */
export function reportError(
  error: unknown,
  message?: string,
  options: ReportErrorOptions = {},
): void {
  const { source = 'app', severity = 'error', context } = options;

  // Filter out auth errors (expected, not actionable)
  if (isAuthError(error)) return;

  if (import.meta.env.DEV) {
    const prefix = `[${source}]`;
    if (severity === 'warning') {
      console.warn(prefix, message || 'Warning', error);
    } else {
      console.error(prefix, message || 'Error', error);
    }
    return;
  }

  // Production: send to Sentry
  Sentry.withScope((scope) => {
    scope.setTag('source', source);
    scope.setLevel(severity === 'warning' ? 'warning' : 'error');
    if (context) {
      scope.setContext('details', context);
    }
    if (message) {
      scope.setExtra('message', message);
    }
    if (error instanceof Error) {
      Sentry.captureException(error);
    } else {
      Sentry.captureMessage(message || String(error));
    }
  });
}

function isAuthError(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return msg.includes('unauthorized') || msg.includes('unauthenticated') || msg.includes('token');
  }
  return false;
}
